<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Processes</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Processes</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewProcess" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Process</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callHelp" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-question-circle mr-2"></i>
                                    <span>Help</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callSupport" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-info-circle mr-2"></i>
                                    <span>Contact Support</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="processes"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'name']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on ID or Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No processes found.
                        </template>

                        <template #loading>
                            Loading processes. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <Column field="id" header="ID" :sortable="true"/>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Process {{ this.process.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="process.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <Editor v-model="process.description" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="input">Input</label>
                        <Editor v-model="process.input" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="processing">Processing</label>
                        <Editor v-model="process.processing" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="output">Output</label>
                        <Editor v-model="process.output" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="process.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateProcess"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Process {{ this.process.name }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Process?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteProcess"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Process</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newProcess.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <Editor v-model="newProcess.description" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="input">Input</label>
                        <Editor v-model="newProcess.input" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="processing">Processing</label>
                        <Editor v-model="newProcess.processing" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="output">Output</label>
                        <Editor v-model="newProcess.output" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                            <span class="ql-formats">
                                              <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                              <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                              <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                              <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                              <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                              <button class="ql-list" value="bullet"
                                                      v-tooltip.bottom="'Bulleted List'"></button>
                                              <button class="ql-list" value="ordered"
                                                      v-tooltip.bottom="'Ordered List'"></button>
                                              <button class="ql-indent" value="-1"
                                                      v-tooltip.bottom="'Indent -1'"></button>
                                              <button class="ql-indent" value="+1"
                                                      v-tooltip.bottom="'Indent +1'"></button>
                                              <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                              <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                              <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                            </span>
                            </template>
                        </Editor>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createProcess"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import ProcessService from "@/service/ProcessService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'name', header: 'Name', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'name', header: 'Name', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {

        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            process: {},
            newProcess: {},
            processes: null,
            originalProcesses: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ]
        }
    },
    watch: {
        processes() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id)
                ]);

                this.userConfiguration = userConfigData.data;
                console.log("[PhoneCalls][mounted] userConfigData.data = ", userConfigData.data);
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find user config for user = ", this.userStore.user.id);
            }

            try {
                const [processData, companyConfigData] = await Promise.all([
                    ProcessService.getCompanyProcesses(this.profile.companyId),
                    CompanyService.getCompanyConfiguration(this.profile.companyId)
                ]);

                this.processes = processData.data;
                this.originalProcesses = processData.data;
                console.log("[PhoneCalls][mounted] processData.data = ", processData.data);

                this.companyConfiguration = companyConfigData.data;
                console.log("[PhoneCalls][mounted] companyConfigData.data = ", companyConfigData.data);

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            if (this.$route.query.new === "1") {
                this.callNewProcess();
            }

            console.log("[PhoneCalls][mounted] this.processes = ", this.processes);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewProcess() {
            this.newProcess = {};
            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callUpdateProcess(data) {
            this.activeTab = 2;
            this.process = data;
        },
        callDeleteProcess(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.process = data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.processes !== null) {

                if (isActive) {
                    this.processes = this.originalProcesses.filter(process => process.status === 'A');
                } else if (isInactive) {
                    this.processes = this.originalProcesses.filter(process => process.status === 'I');
                } else {
                    this.processes = this.originalProcesses;
                }

            } else {
                // Handle the case when auditTrails is null
                this.processes = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateProcess(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteProcess(data);
                }
            });

            console.log('[PhoneCalls][getFilteredItems] items = ', items);

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async updateProcess() {
            console.log("[PhoneCalls][updatePhoneCall] Start.");

            this.submitted = true;

            if (this.process.name.trim()) {

                try {

                    console.log('[PhoneCalls][updatePhoneCall] this.process = ', this.process);

                    var processDto = {
                        companyId: this.profile.companyId,
                        name: this.process.name,
                        description: this.process.description,
                        input: this.process.input,
                        processing: this.process.processing,
                        output: this.process.output,
                        status: this.process.status
                    };

                    const response = await ProcessService.updateProcess(this.process.id, processDto);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    const [processData] = await Promise.all([
                        ProcessService.getCompanyProcesses(this.profile.companyId)
                    ]);

                    this.processes = processData.data;
                    this.originalProcesses = processData.data;

                    console.log('[PhoneCalls][updatePhoneCall] this.processes = ', this.processes);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated process',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update process. Please try again.',
                        life: 3000
                    });
                }
            }

            this.process = {};
            this.activeTab = 1;
            this.submitted = false;

        },
        async deleteProcess() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.process.id) {

                    try {
                        const response = await ProcessService.deleteProcess(this.process.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [processData] = await Promise.all([
                            ProcessService.getCompanyProcesses(this.profile.companyId)
                        ]);

                        this.processes = processData.data;
                        this.originalProcesses = processData.data;

                        console.log('[PhoneCalls][updatePhoneCall] this.processes = ', this.processes);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted process',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete process. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createProcess() {
            console.log("[PhoneCalls][createProcess] Start.");

            this.submitted = true;

            if (this.newProcess.name.trim()) {

                try {

                    console.log('[PhoneCalls][updatePhoneCall] this.newProcess = ', this.newProcess);

                    var newProcessDto = {
                        companyId: this.profile.companyId,
                        name: this.newProcess.name,
                        description: this.newProcess.description,
                        input: this.newProcess.input,
                        processing: this.newProcess.processing,
                        output: this.newProcess.output
                    };

                    console.log("[" + name + "][saveNewProcess] Creating Process. Data = ", newProcessDto);

                    const response = await ProcessService.createProcess(this.profile.companyId, newProcessDto);
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    const [processData] = await Promise.all([
                        ProcessService.getCompanyProcesses(this.profile.companyId)
                    ]);

                    this.processes = processData.data;
                    this.originalProcesses = processData.data;

                    console.log('[PhoneCalls][updatePhoneCall] this.processes = ', this.processes);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created process',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create process. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newProcess = {};
            this.activeTab = 1;
            this.submitted = false;

        },
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FFCDD2;
        color: #C63737;
    }
}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
